<template>
  <div class="comments">
    <div class="comments__header">
      <h1>All Comments</h1>
      <div class="comments__stats" v-if="allComments">
        <span>Showing {{ allComments.data.length }} comments</span>
        <span v-if="allComments.hasMore">• More available</span>
      </div>
    </div>

    <div v-if="$apollo.loading && !allComments" class="comments__loading">Loading comments...</div>

    <div v-else-if="allComments" class="comments__content">
      <div class="comments__list">
        <user-comment-cell v-for="comment in allComments.data" :key="comment.id" :comment="comment" />
      </div>

      <div v-if="allComments.hasMore" class="comments__load-more">
        <base-button @click="loadMore" :disabled="loadingMore">
          {{ loadingMore ? 'Loading...' : 'Load More' }}
        </base-button>
      </div>

      <div v-if="allComments.data.length === 0" class="comments__empty">No comments found.</div>
    </div>

    <div v-else class="comments__error">Failed to load comments. Please try again.</div>
  </div>
</template>

<script lang="ts">
import UserCommentCell from '@/components/UserCommentCell.vue';
import gql from 'graphql-tag';
import { Component, Vue } from 'vue-property-decorator';
import { CommentsPage } from '../types/api';

@Component({
  components: { UserCommentCell },
  apollo: {
    allComments: {
      query: gql`
        query AllComments($pagination: CommentsPageInput) {
          allComments(pagination: $pagination) {
            data {
              id
              body
              createdAt
              deletedAt
              userProfile {
                id
                username
                role
              }
              chat {
                id
                target {
                  ... on Blog {
                    __typename
                    id
                    title
                  }
                  ... on Show {
                    __typename
                    id
                    showDate
                    address {
                      venue
                      city
                    }
                  }
                }
              }
            }
            hasMore
          }
        }
      `,
      variables() {
        return {
          pagination: {
            skip: 0,
            take: 50,
          },
        };
      },
      fetchPolicy: 'cache-and-network',
    },
  },
})
export default class Comments extends Vue {
  allComments?: CommentsPage;
  loadingMore = false;

  async loadMore() {
    if (!this.allComments || this.loadingMore) return;

    this.loadingMore = true;
    try {
      await this.$apollo.queries.allComments.fetchMore({
        variables: {
          pagination: {
            skip: this.allComments.data.length,
            take: 50,
          },
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult?.allComments) return previousResult;

          const previousComments = previousResult.allComments.data;
          const newComments = fetchMoreResult.allComments.data;

          return {
            allComments: {
              ...fetchMoreResult.allComments,
              data: [...previousComments, ...newComments],
            },
          };
        },
      });
    } finally {
      this.loadingMore = false;
    }
  }

  // UserCommentCell now refetches both User and AllComments queries when deleting
}
</script>

<style lang="scss" scoped>
.comments {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 16px;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;

    h1 {
      margin: 0;
      color: $color-theme-red;
    }
  }

  &__stats {
    display: flex;
    gap: 8px;
    color: $color-disabled;
    font-size: 0.9em;
  }

  &__loading,
  &__error {
    padding: 32px;
    text-align: center;
    color: $color-disabled;
    font-style: italic;
  }

  &__error {
    color: $color-theme-red;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__load-more {
    display: flex;
    justify-content: center;
    padding: 16px;
  }

  &__empty {
    padding: 32px;
    text-align: center;
    color: $color-disabled;
    font-style: italic;
  }
}
</style>
